import { ModuleKey } from './types';

// Display metadata for the module rail, upsell card, and Tenant Provisioning checkboxes.
// Keyed by every ModuleKey so callers never hit an undefined lookup.
export const MODULE_META: Record<ModuleKey, { label: string; blurb: string }> = {
    attendance: { label: 'Attendance', blurb: 'Clock-ins, lateness, overtime and daily presence.' },
    leave: { label: 'Leave', blurb: 'Leave applications, approvals and balances.' },
    people: { label: 'People', blurb: 'Headcount, departments and roster composition.' },
    holidays: { label: 'Holidays', blurb: 'Institution holiday calendar.' },
    accounting: { label: 'Accounting', blurb: 'Chart of accounts, journals, AR/AP and cash position.' },
    inventory: { label: 'Inventory', blurb: 'Stock value, item counts and low-stock alerts.' },
    sales: { label: 'Sales', blurb: 'Pipeline value and month-to-date sales.' },
    payroll: { label: 'Payroll', blurb: 'Payroll runs, totals and headcount on last run.' },
    expense: { label: 'Expense', blurb: 'Expense claims, approvals and reimbursement totals.' },
    merchandising: { label: 'Merchandising', blurb: 'Buyers, styles, samples and the order book.' },
    commercial: { label: 'Commercial', blurb: 'Export LCs, back-to-back LCs, EXP/UD registers and cash incentive claims.' },
    fixed_assets: { label: 'Fixed Assets', blurb: 'Asset register, counts, transfers, requisitions and depreciation.' },
    maintenance: { label: 'Maintenance', blurb: 'Equipment register, breakdown requests, work orders and preventive maintenance.' },
    shifts: { label: 'Shifts', blurb: 'Shift definitions, rosters and rotations. Off = flat attendance.' },
    procurement: { label: 'Procurement', blurb: 'Suppliers, purchase orders, receipts, bills and payments.' },
    tax: { label: 'Tax', blurb: 'Employee income tax slabs, exemptions and reports.' },
    insurance: { label: 'Insurance', blurb: 'Group insurance policy and enrollments (BLA §99).' },
    tasks: { label: 'Tasks', blurb: 'Assign, track and complete work items.' },
    notices: { label: 'Notices', blurb: 'Publish and read institution announcements.' },
    reports: { label: 'Reports', blurb: 'Employee consumption and statutory registers.' },
    help: { label: 'Help', blurb: 'The ? button that explains every screen, field and button.' },
    feedback: { label: 'Feedback', blurb: 'The button users press to send a bug, idea or question to Kervolt.' },
    xero: { label: 'Xero', blurb: 'Sends issued invoices and receipts to the institution\'s own Xero organisation.' },
};

// Canonical gated module set (mirrors backend com.kervolt.oms.common.DashboardModule.keys()).
// Order = Tenant Provisioning checkbox order.
export const GATED_MODULE_KEYS: ModuleKey[] = [
    'attendance', 'shifts', 'leave', 'holidays', 'people', 'accounting', 'inventory', 'procurement',
    'sales', 'payroll', 'tax', 'insurance', 'expense', 'merchandising', 'commercial', 'maintenance',
    'fixed_assets', 'tasks', 'notices', 'reports', 'help', 'feedback', 'xero',
];

// Fired on window after the institution admin switches modules on or off, so the menu re-reads them.
export const MODULES_CHANGED_EVENT = 'oms:modules-changed';

// Modules on the free plan (mirrors backend com.kervolt.oms.common.FreeTierPolicy.ALLOWED_MODULES);
// a new institution starts FREE with exactly these switched on.
export const FREE_PLAN_MODULES: ModuleKey[] = [
    'attendance', 'shifts', 'leave', 'holidays', 'people', 'insurance', 'tasks', 'notices', 'reports',
    'help', 'feedback',
];

// Core HR is a pricing label, not a plan tier (the tier stays PAID): the free modules plus payroll.
// See docs/decisions/2026-08-14-saas-pricing-model.md. Everything else is a paid add-on.
export const CORE_HR_MODULES: ModuleKey[] = [...FREE_PLAN_MODULES, 'payroll'];

/**
 * Whether a module participates in institution module-gating at all.
 *
 * Since 2026-10-23 every ModuleKey is gated; kept so boards stay safe if an ungated key is
 * ever added back (an ungated module is absent from `enabledModules` and must stay visible).
 */
export function isGatedModule(module: ModuleKey): boolean {
    return GATED_MODULE_KEYS.includes(module);
}
